/**
 * Shared, site-wide campaign attribution with a ~30 day first-touch window.
 *
 * Browser-safe (no server imports). Every lead form on the site reads this so
 * the Admin lead shows the campaign that originally acquired the visitor, even
 * if they came back days later through a direct visit.
 *
 * `_fbp` / `_fbc` are never persisted here — they are always re-read fresh from
 * cookies at conversion time.
 */
import { z } from "zod";

export type Touch = {
  landing_page: string;
  referrer: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  utm_term: string;
  fbclid: string;
  oppref: string;
  ts: number;
};

export type MetaAttribution = {
  /** Flat fields reflect the FIRST touch (what acquired the visitor). */
  landing_page: string;
  referrer: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  utm_term: string;
  fbclid: string;
  /** OpenAI / ChatGPT Ads click reference, stored as hidden metadata. */
  oppref: string;
  /** Fresh from cookies, never from storage. */
  fbp: string;
  fbc: string;
  first_touch?: Touch;
  last_touch?: Touch;
  /** Funnel labels supplied by the form that submits. */
  funnel?: string;
  lead_type?: string;
  form_name?: string;
};

const STORAGE_KEY = "durafoam_attribution_v1";
export const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const EMPTY_TOUCH: Touch = {
  landing_page: "",
  referrer: "",
  utm_source: "",
  utm_medium: "",
  utm_campaign: "",
  utm_content: "",
  utm_term: "",
  fbclid: "",
  oppref: "",
  ts: 0,
};

function readCookie(name: string): string {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1] ?? "") : "";
}

function isInternalReferrer(referrer: string): boolean {
  if (!referrer) return true;
  try {
    return new URL(referrer).hostname === window.location.hostname;
  } catch {
    return false;
  }
}

/** Case-insensitive query param lookup; values are preserved as decoded. */
function param(p: URLSearchParams, name: string): string {
  const want = name.toLowerCase();
  for (const [k, v] of p.entries()) {
    if (k.toLowerCase() === want && v) return v;
  }
  return "";
}

/** Reads the current URL/referrer into a Touch. */
function currentTouch(): Touch {
  if (typeof window === "undefined") return EMPTY_TOUCH;
  const p = new URLSearchParams(window.location.search);
  return {
    landing_page: window.location.pathname + window.location.search,
    referrer: typeof document !== "undefined" ? document.referrer : "",
    utm_source: param(p, "utm_source"),
    utm_medium: param(p, "utm_medium"),
    utm_campaign: param(p, "utm_campaign"),
    utm_content: param(p, "utm_content"),
    utm_term: param(p, "utm_term"),
    fbclid: param(p, "fbclid"),
    oppref: param(p, "oppref"),
    ts: Date.now(),
  };
}

/** A touch worth recording: paid click params, or an external referrer. */
function isMeaningful(t: Touch): boolean {
  if (t.utm_source || t.utm_medium || t.utm_campaign || t.fbclid || t.oppref) return true;
  return !isInternalReferrer(t.referrer);
}

type Stored = { first?: Touch; last?: Touch };

function readStored(): Stored {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Stored;
    const first = parsed.first;
    // Expire the whole record once the first-touch window closes.
    if (!first || typeof first.ts !== "number" || Date.now() - first.ts > ATTRIBUTION_TTL_MS) {
      return {};
    }
    return parsed;
  } catch {
    return {};
  }
}

function writeStored(value: Stored): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* storage disabled / full — attribution degrades to current visit */
  }
}

/**
 * Call once per page load. Writes first-touch the first time (and again only
 * after the 30-day window expires), and refreshes last-touch whenever a new
 * meaningful source appears.
 */
export function captureOnLanding(): void {
  if (typeof window === "undefined") return;
  const now = currentTouch();
  const stored = readStored();
  const meaningful = isMeaningful(now);

  if (!stored.first) {
    // First ever (or post-expiry) visit — record it even if it's direct traffic.
    writeStored({ first: now, last: meaningful ? now : undefined });
    return;
  }
  if (meaningful) writeStored({ first: stored.first, last: now });
}

/**
 * Attribution payload for a conversion. First touch wins for the flat fields;
 * `_fbp` / `_fbc` come straight from cookies.
 */
export function getAttribution(labels?: {
  funnel?: string;
  lead_type?: string;
  form_name?: string;
}): MetaAttribution {
  const now = typeof window === "undefined" ? EMPTY_TOUCH : currentTouch();
  const stored = readStored();
  const first = stored.first ?? (isMeaningful(now) ? now : now);
  const last = stored.last ?? now;

  const fbclid = first.fbclid || last.fbclid || now.fbclid;
  const cookieFbc = readCookie("_fbc");
  // Only derive an _fbc when a real Facebook click id exists — never fabricate.
  const fbc = cookieFbc || (fbclid ? `fb.1.${first.ts || Date.now()}.${fbclid}` : "");

  return {
    landing_page: first.landing_page || now.landing_page,
    referrer: first.referrer,
    utm_source: first.utm_source || last.utm_source,
    utm_medium: first.utm_medium || last.utm_medium,
    utm_campaign: first.utm_campaign || last.utm_campaign,
    utm_content: first.utm_content || last.utm_content,
    utm_term: first.utm_term || last.utm_term,
    fbclid,
    oppref: first.oppref || last.oppref || now.oppref,
    fbp: readCookie("_fbp"),
    fbc,
    first_touch: first,
    last_touch: last,
    ...(labels ?? {}),
  };
}

/** True when this visitor arrived through a Meta / paid click. */
export function isPaidMeta(a: Pick<MetaAttribution, "utm_source" | "utm_medium" | "fbclid">): boolean {
  const src = (a.utm_source || "").toLowerCase();
  const med = (a.utm_medium || "").toLowerCase();
  if (a.fbclid) return true;
  if (/facebook|instagram|meta|fb|ig/.test(src)) return true;
  return /paid|cpc|ppc|social_paid|paidsocial/.test(med);
}

/* ------------------------- server-side validation ------------------------- */

const str = z.string().trim().max(500).optional().or(z.literal(""));

const touchSchema = z
  .object({
    landing_page: str,
    referrer: str,
    utm_source: str,
    utm_medium: str,
    utm_campaign: str,
    utm_content: str,
    utm_term: str,
    fbclid: str,
    oppref: str,
    ts: z.number().optional(),
  })
  .partial();

export const metaAttributionSchema = z
  .object({
    landing_page: str,
    referrer: str,
    utm_source: str,
    utm_medium: str,
    utm_campaign: str,
    utm_content: str,
    utm_term: str,
    fbclid: str,
    oppref: str,
    fbp: str,
    fbc: str,
    funnel: z.string().trim().max(60).optional().or(z.literal("")),
    lead_type: z.string().trim().max(60).optional().or(z.literal("")),
    form_name: z.string().trim().max(80).optional().or(z.literal("")),
    first_touch: touchSchema.optional(),
    last_touch: touchSchema.optional(),
  })
  .partial()
  .optional();

export type ServerAttribution = NonNullable<z.infer<typeof metaAttributionSchema>>;

/** Human-readable, one-value-per-line attribution block stored with the lead. */
export function attributionNoteLines(a: ServerAttribution | undefined): string[] {
  const v = a ?? {};
  const none = (x?: string) => (x && x.length ? x : "(none)");
  const touch = (t?: ServerAttribution["first_touch"]) => {
    if (!t) return "(none)";
    const parts = [t.utm_source || t.referrer || "direct", t.utm_campaign, t.landing_page].filter(
      Boolean,
    );
    return parts.join(" | ") || "(none)";
  };
  return [
    `— Campaign attribution —`,
    `funnel: ${none(v.funnel)}`,
    `lead_type: ${none(v.lead_type)}`,
    `form_name: ${none(v.form_name)}`,
    `landing_page: ${none(v.landing_page)}`,
    `referrer: ${none(v.referrer)}`,
    `utm_source: ${none(v.utm_source)}`,
    `utm_medium: ${none(v.utm_medium)}`,
    `utm_campaign: ${none(v.utm_campaign)}`,
    `utm_content: ${none(v.utm_content)}`,
    `utm_term: ${none(v.utm_term)}`,
    `fbclid: ${none(v.fbclid)}`,
    `first_touch: ${touch(v.first_touch)}`,
    `last_touch: ${touch(v.last_touch)}`,
  ];
}

/** True when this visitor arrived through a ChatGPT / OpenAI ad. */
export function isChatGptAds(a: Pick<ServerAttribution, "utm_source" | "oppref">): boolean {
  const src = (a.utm_source || "").toLowerCase();
  if (src === "chatgpt" || src === "openai") return true;
  return !src && !!a.oppref;
}

/**
 * Human-readable channel label used by the admin UI and conversion reporting.
 * Existing channels keep their labels; ChatGPT Ads is the new one.
 */
export function normalizedSource(a: ServerAttribution | undefined): string {
  const v = a ?? {};
  if (isChatGptAds(v)) return "ChatGPT Ads";
  const src = (v.utm_source || "").toLowerCase();
  const med = (v.utm_medium || "").toLowerCase();
  if (/google/.test(src) && /cpc|ppc|paid/.test(med)) return "Google Ads";
  if (v.fbclid || /facebook|instagram|meta|fb|ig/.test(src)) return "Meta Ads";
  if (/paid|cpc|ppc/.test(med)) return "Paid";
  if (src) return v.utm_source ?? "";
  if (/organic/.test(med)) return "Organic";
  if (v.referrer) {
    try {
      return `Referral: ${new URL(v.referrer).hostname}`;
    } catch {
      return "Referral";
    }
  }
  return "Direct";
}

/** Paid-channel lead source, otherwise the caller's own source value. */
export function leadSourceFor(a: ServerAttribution | undefined, fallback: string): string {
  const v = a ?? {};
  if (isChatGptAds(v)) return "chatgpt-ads";
  return isPaidMeta({
    utm_source: v.utm_source ?? "",
    utm_medium: v.utm_medium ?? "",
    fbclid: v.fbclid ?? "",
  })
    ? "meta-ads"
    : fallback;
}
